
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';

export interface Notification {
  id: string;
  type: 'message' | 'swap_proposal';
  title: string;
  message: string;
  timestamp: string;
  isRead: boolean;
  data?: any;
}

export function useNotifications(userId: string | null) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [channel, setChannel] = useState<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    // Subscribe to new messages
    const messagesChannel = supabase
      .channel('messages-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `receiver_id=eq.${userId}`,
        },
        async (payload) => {
          const newMessage = payload.new;

          // Fetch sender info
          const { data: senderProfile, error: senderError } = await supabase
            .from('profiles')
            .select('username, full_name')
            .eq('id', newMessage.sender_id)
            .maybeSingle();

          if (senderError) {
            console.error('Failed to fetch sender profile:', senderError);
            return;
          }

          const senderName = senderProfile?.full_name || senderProfile?.username || 'Someone';

          const notification: Notification = {
            id: `msg-${newMessage.id}`,
            type: 'message',
            title: 'New Message',
            message: `${senderName} sent you a message`,
            timestamp: newMessage.created_at,
            isRead: false,
            data: newMessage,
          };

          setNotifications((prev) => [notification, ...prev]);
          setUnreadCount((prev) => prev + 1);

          showBrowserNotification(notification.title, notification.message);
          playNotificationSound();
        }
      )
      .subscribe();

    // Subscribe to new swap proposals
    const proposalsChannel = supabase
      .channel('proposals-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'swap_proposals',
          filter: `receiver_id=eq.${userId}`,
        },
        async (payload) => {
          const newProposal = payload.new;

          // Fetch proposer info
          const { data: proposerProfile, error: proposerError } = await supabase
            .from('profiles')
            .select('username, full_name')
            .eq('id', newProposal.proposer_id)
            .maybeSingle();

          if (proposerError) {
            console.error('Failed to fetch proposer profile:', proposerError);
            return;
          }

          const proposerName = proposerProfile?.full_name || proposerProfile?.username || 'Someone';

          const notification: Notification = {
            id: `proposal-${newProposal.id}`,
            type: 'swap_proposal',
            title: 'New Swap Proposal',
            message: `${proposerName} sent you a swap proposal`,
            timestamp: newProposal.created_at,
            isRead: false,
            data: newProposal,
          };

          setNotifications((prev) => [notification, ...prev]);
          setUnreadCount((prev) => prev + 1);

          showBrowserNotification(notification.title, notification.message);
          playNotificationSound();
        }
      )
      .subscribe();

    // Subscribe to proposal status updates
    const proposalUpdatesChannel = supabase
      .channel('proposal-updates-notifications')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'swap_proposals',
          filter: `proposer_id=eq.${userId}`,
        },
        async (payload) => {
          const updatedProposal = payload.new;
          const oldProposal = payload.old;

          // Only notify if status changed and is no longer pending
          if (updatedProposal.status !== oldProposal.status && updatedProposal.status !== 'pending') {
            // Fetch receiver info
            const { data: receiverProfile, error: receiverError } = await supabase
              .from('profiles')
              .select('username, full_name')
              .eq('id', updatedProposal.receiver_id)
              .maybeSingle();

            if (receiverError) {
              console.error('Failed to fetch receiver profile:', receiverError);
              return;
            }

            const receiverName = receiverProfile?.full_name || receiverProfile?.username || 'Someone';
            const statusText = updatedProposal.status === 'accepted' ? 'accepted' : 'declined';

            const notification: Notification = {
              id: `proposal-update-${updatedProposal.id}`,
              type: 'swap_proposal',
              title: `Proposal ${statusText.charAt(0).toUpperCase() + statusText.slice(1)}`,
              message: `${receiverName} ${statusText} your swap proposal`,
              timestamp: updatedProposal.updated_at,
              isRead: false,
              data: updatedProposal,
            };

            setNotifications((prev) => [notification, ...prev]);
            setUnreadCount((prev) => prev + 1);

            showBrowserNotification(notification.title, notification.message);
            playNotificationSound();
          }
        }
      )
      .subscribe();

    // Keep a reference to the primary channel (optional, can be used elsewhere)
    setChannel(messagesChannel);

    // Cleanup on unmount or when userId changes
    return () => {
      messagesChannel.unsubscribe();
      proposalsChannel.unsubscribe();
      proposalUpdatesChannel.unsubscribe();
    };
  }, [userId]);

  const markAsRead = (notificationId: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);
  };

  const clearNotification = (notificationId: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    const notification = notifications.find((n) => n.id === notificationId);
    if (notification && !notification.isRead) {
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
  };

  const clearAllNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotification,
    clearAllNotifications,
  };
}

// Helper function to show browser notifications
function showBrowserNotification(title: string, body: string) {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, {
      body,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: 'swaphub-notification',
      requireInteraction: false,
    });
  }
}

// Helper function to play notification sound
function playNotificationSound() {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (error) {
    console.error('Error playing notification sound:', error);
  }
}

// Request notification permission
export function requestNotificationPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
}
